import { ArrowRightIcon } from "@heroicons/react/20/solid";
import type { PropsWithChildren, ReactNode } from "react";
import { useMemo } from "react";
import type { TeamData } from "robotevents";
import { LinkButton } from "~components/Button";
import { Incident as IncidentComponent } from "~components/Incident";
import { InspectionChip } from "~components/Inspection";
import { MatchTime } from "~components/Match";
import { RulesSummary } from "~components/RulesSummary";
import type { Incident } from "~utils/data/incident";
import { getUpcomingMatch } from "~utils/data/robotevents";
import { useTeamInspectionStatus } from "~utils/hooks/incident";
import { useEvent, useEventMatchesForTeam } from "~utils/hooks/robotevents";

export type TeamMenuProps = PropsWithChildren<{
	team: TeamData;
	sku: string;
	incidents: Incident[];
	filter?: (incident: Incident) => boolean;
	headerAside?: ReactNode;
	inspectionChipPlacement?: "header" | "body";
}>;

export const TeamMenu: React.FC<TeamMenuProps> = ({
	team,
	sku,
	incidents,
	children,
	filter = (incident) => incident.team === team.number,
	headerAside,
	inspectionChipPlacement = "header",
}) => {
	const inspectionStatus = useTeamInspectionStatus(team.number, sku);
	const { data: event } = useEvent(sku);
	const { data: matches } = useEventMatchesForTeam(event, team);
	const match = useMemo(() => getUpcomingMatch(matches ?? []), [matches]);
	const inspectionChip = <InspectionChip status={inspectionStatus.status} />;

	return (
		<nav className="mt-2">
			<div className="flex items-center justify-between gap-4 mb-4">
				<p className="overflow-hidden whitespace-nowrap text-ellipsis max-w-full flex-1">
					<span className="font-mono text-emerald-400">{team.number}</span>
					{" • "}
					<span>{team.team_name}</span>
				</p>
				<div className="flex items-center gap-4">
					{headerAside}
					{inspectionChipPlacement === "header" ? inspectionChip : null}
				</div>
			</div>
			{inspectionStatus.status === "failed" && inspectionStatus.incident ? (
				<IncidentComponent
					incident={inspectionStatus.incident}
					className="max-h-20 overflow-hidden mb-4"
				/>
			) : null}
			{inspectionChipPlacement === "body" ? (
				<div className="mb-4">{inspectionChip}</div>
			) : null}
			<RulesSummary
				className="break-all"
				incidents={incidents}
				filter={filter}
			/>
			{match ? (
				<section>
					<span>{match.division.name}</span> • <span>{match.name}</span> •{" "}
					<MatchTime match={match} />
				</section>
			) : null}
			<LinkButton
				to={"/$sku/team/$team"}
				params={{ sku, team: team.number }}
				className="w-full mt-4 flex items-center"
			>
				<span className="flex-1">Details</span>
				<ArrowRightIcon height={20} className="text-emerald-400" />
			</LinkButton>
			<hr className="mt-4 border-zinc-600" />
			{children}
		</nav>
	);
};
